import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePublicAuth } from "@/contexts/PublicAuthContext";
import {
  ClipboardCheck, Sparkles, Check, Info, ChevronDown, ChevronUp,
  HelpCircle, FileText, ArrowRight, Download, Filter, Clock, Calendar,
  AlertCircle, ShieldAlert, ShieldCheck, Building2, Lightbulb
} from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";

const BRANCH_DETAILS = [
  { city: "Latur", phones: "+91 93095 53235, +91 99708 09003" },
  { city: "Pune", phones: "+91 80878 13700, +91 96654 75432" },
  { city: "Kolhapur", phones: "+91 88550 85858" },
  { city: "Nanded", phones: "+91 89830 01441, +91 77988 09365" },
  { city: "Solapur", phones: "+91 99237 54628" }
];
// ─── Enriched Document Data Setup ────────────────────────────────────────────────────────

const MANDATORY_DOCS = [
  {
    id: "man_photo",
    name: "Students 8-10 Passport Size Color Photographs",
    description: "NEET फॉर्म भरताना दिलेला फोटो Compulsory आहे. फोटोवर नाव असले तरी चालेल आणि नसले तरी चालेल.",
    issuer: "Candidate / Professional Studio",
    validity: "Recently used for NEET Application form.",
    tips: "15–20 Passport Size Color Photographs प्रवेश प्रक्रियेसाठी आवश्यक आहेत. Keep a digital copy handy in JPEG format.",
    guidelines: [
      "NEET फॉर्म भरताना दिलेला फोटो वापरावा",
      "15–20 Passport Size Color Photographs प्रवेश प्रक्रियेसाठी आवश्यक आहेत",
      "White background असलेला (recent) फोटो असावा",
      "Digital copy (JPEG format) Google Drive वर सेव्ह करून ठेवा"
    ]
  },
  {
    id: "man_aadhaar",
    name: "Students Aadhaar Card",
    description: "आधार कार्ड वरचे नाव मागे पुढे असले तरी चालेल पण नावामध्ये spelling mistake असू नये.",
    issuer: "UIDAI (Govt of India)",
    validity: "Lifetime",
    tips: "आधार कार्ड वरचा फोटो updated असावा. जन्म तारखे मध्ये चूक असू नये. आधार कार्ड वरचा पत्ता बरोबर असावा — spelling mistake असू नये.",
    guidelines: [
      "आधार कार्ड वरचे नाव मागे पुढे असले तरी चालेल, पण नावामध्ये spelling mistake असू नये",
      "आधार कार्ड वरचा फोटो updated असावा",
      "जन्म तारखे मध्ये (Date of Birth) चूक असू नये",
      "आधार कार्ड वरचा पत्ता बरोबर असावा — spelling mistake असू नये,असेल तर ते दुरुस्त करून घ्यावे",
      "Mobile number आधार कार्डशी linked असणे आवश्यक आहे (OTP साठी)"
    ]
  },
  {
    id: "man_neet_conf",
    name: "NEET-UG-2026 Confirmation Page",
    description: "नीट चा फॉर्म भरलेली पावती — NEET-UG online application confirmation page printout.",
    issuer: "NTA (National Testing Agency)",
    validity: "For Admission Year 2026",
    tips: "If lost, download again from the official NEET dashboard (neet.nta.nic.in).",
    guidelines: [
      "नीट चा फॉर्म भरलेली पावती (Confirmation Page) असणे Compulsory आहे",
      "NTA च्या official website वरून print काढावी",
      "गरज पडल्यास NEET dashboard वरून पुन्हा download करता येते"
    ]
  },
  {
    id: "man_neet_admit",
    name: "Duly Filled NEET-UG-2026 Admit Card",
    description: "NEET Admit Card ज्यावर Passport size photograph विद्यार्थ्यांच्या डाव्या हातातचा ठसा आणि सही असेल.",
    issuer: "NTA (National Testing Agency)",
    validity: "For Admission Year 2026"
  },
  {
    id: "man_neet_score",
    name: "NEET-UG-2026 Online Score Card Print",
    description: "NEET Result Score Card printout — ज्यावर Total Score, Percentile, आणि All India Rank स्पष्ट दिसत असावा.",
    issuer: "NTA (National Testing Agency)",
    validity: "For Admission Year 2026",
    tips: "Score Card वर Absolute NEET Score, Percentile आणि AIR स्पष्टपणे दिसत असणे आवश्यक आहे."
  },
  {
    id: "man_ssc_marks",
    name: "SSC/Std. X or Its Equivalent Marks Memo",
    description: "10वीची गुणपत्रिका. नावाची spelling Aadhaar Card शी जुळणे आवश्यक आहे.",
    issuer: "State Board / CBSE / ICSE",
    validity: "Lifetime",
    tips: "SSC Marks Memo वरील नाव व जन्म तारीख Aadhaar Card शी जुळत असल्याची खात्री करा. Original document सोबत ठेवा.",
  },
  {
    id: "man_ssc_cert",
    name: "SSC/Std. X or Its Equivalent Pass/Board Certificate",
    description: "10वीचे Board Passing Certificate — मुख्यतः Date of Birth verify करण्यासाठी वापरले जाते.",
    issuer: "State Board / CBSE / ICSE",
    validity: "Lifetime",
    tips: "Board Certificate वरील नाव व जन्म तारीख Aadhaar Card शी जुळत असल्याची खात्री करा.",
    guidelines: [
      "SSC / 10वीचे Board Passing Certificate असणे Compulsory आहे",
      "Date of Birth verify करण्यासाठी हे document प्रामुख्याने वापरले जाते",
      "नाव आणि जन्म तारीख इतर documents शी जुळणे आवश्यक आहे"
    ]
  },
  {
    id: "man_hsc_marks",
    name: "HSC/Std. XII or Its Equivalent Marks Memo",
    description: "12वीची गुणपत्रिका. नावाची spelling Aadhaar Card शी जुळणे आवश्यक",
    issuer: "State Board / CBSE / ICSE",
    validity: "Lifetime",
    tips: "Physics, Chemistry आणि Biology मध्ये required aggregate percentage असल्याची खात्री करा.",
    guidelines: [
      "Physics, Chemistry, Biology मध्ये required aggregate % असणे आवश्यक आहे",
      "Leaving Certificate (LC/TC) सोबत या document ची spellings जुळत असल्याची खात्री करा"
    ]
  },
  {
    id: "man_nat_dom",
    name: "Nationality cum Domicile Certificate",
    description: "Maharashtra Domicile आणि Indian Nationality verify करण्यासाठी Tahsildar / SDM यांनी दिलेले certificate.",
    issuer: "Tahsildar / Sub-Divisional Magistrate (SDM)",
    validity: "Lifetime",
    tips: "Certificate मध्ये 'Domiciled in the State of Maharashtra' असे स्पष्ट नमूद असणे आवश्यक आहे.",
    guidelines: [
      "Nationality cum Domicile Certificate — Tahsildar किंवा SDM यांनी issued असणे आवश्यक",
      "Certificate मध्ये 'Domiciled in the State of Maharashtra' स्पष्ट नमूद असावे",
      "State Quota seats साठी हे document अनिवार्य आहे",
      "Indian Nationality confirm करणारे separate certificate असल्यास ते सुद्धा सोबत ठेवा"
    ]
  },
  {
    id: "man_lc_tc",
    name: "Class 12th Leaving/Transfer Certificate (LC/TC)",
    description: "Junior College / शाळेने दिलेले original Leaving Certificate.",
    issuer: "Junior College / School Last Attended",
    tips: "LC/TC वर 'Passed HSC / Class 12th' असा उल्लेख आणि institution चा  seal असणे आवश्यक आहे.",
    guidelines: [
      "Class 12th चे original Leaving Certificate / Transfer Certificate (LC/TC) आवश्यक",
      "नावाची spelling Aadhaar Card शी exact जुळणे आवश्यक आहे",
      "'Passed HSC / Class 12th' असा स्पष्ट उल्लेख LC/TC वर असावा",
      "Institution चा official seal आणि Principal ची सही असणे आवश्यक"
    ]
  },
  {
    id: "man_fitness",
    name: "Fitness Certificate",
    description: "Registered Medical Practitioner (MBBS) यांनी दिलेले Fitness Certificate. NEET Result नंतर काढणे योग्य.",
    issuer: "Registered Medical Practitioner (MBBS)",
    validity: "Within 3 months of counselling",
    tips: "Counselling Brochure मध्ये दिलेल्या official format मध्येच Fitness Certificate असणे आवश्यक आहे."
  },
  {
    id: "man_gap",
    name: "Gap Affidavit",
    description: "12th नंतर academic gap असलेल्या सर्व विद्यार्थ्यांना ₹500 stamp paper वर Gap Affidavit Compulsory आहे.",
    issuer: "Notary",
    validity: "Current admission year",
    tips: "Gap year असलेल्या सर्व repeater students साठी हे document Compulsory आहे — ₹500 च्या stamp paper वर Notary/Executive Magistrate यांच्याकडून बनवावे."
  },
  {
    id: "man_minority",
    name: "Religious/Linguistic Minority Affidavit",
    guidelines: [
      "Religious / Linguistic Minority Quota मधून प्रवेश घेणाऱ्यांसाठीच हे document आवश्यक",
      "TC (Leaving Certificate) वर candidate चा  Mother Tongue / Religion नमूद असणे आवश्यक",
      "Notary / Judicial Magistrate यांच्याकडून certified affidavit असावे"
    ]
  },
  {
    id: "man_voter_annex",
    name: "Students Voter ID / Annexure 'C'",
    issuer: "Election Commission of India / Tahsildar",
    validity: "Lifetime",
    tips: "Voter ID नसल्यास Annexure 'C' form चालतो.",
    guidelines: [
      "वय 18 पेक्षा कमी असल्यास Annexure 'C' form Compulsory आहे",
      "Annexure 'C' form चा फॉरमॅट ऍडमिशन Broucher मध्ये मिळेल",
      "हे document secondary identity proof म्हणून वापरले जाते"
    ]
  },
  {
    id: "man_char",
    name: "Character Certificate",
    description: "विद्यार्थ्याच्या वर्तणुकीबाबत हे प्रमाणपत्र - Character Certificate.",
    issuer: "12th High School / Junior College",
    tips: "Institution च्या official letterhead वर,  Principal ची सही असलेले Character Certificate आवश्यक.",

  },
  {
    id: "man_migration",
    name: "Migration Certificate",
    description: "Maharashtra बाहेरील admission घेऊ इच्छिणाऱ्या students साठी Migration Certificate अतिरिक्त लागेल.बारावी उत्तीर्ण झालेल्या कॉलेजमधून तुम्हाला हे प्रमाणपत्र मिळेल",
    issuer: "12th High School / Junior College"
  },
  {
    id: "man_reg_form",
    name: "Admission Process Registration Form",
    description: "MahaCET / State Authority च्या online registration form ची printout आणि fee payment transaction receipt.",
    issuer: "State Common Entrance Test Cell (MahaCET)",
    validity: "For Admission Year 2026",
    tips: "Online registration form ची printout आणि fee payment ची transaction receipt दोन्ही सोबत ठेवा.",
    guidelines: [
      "MahaCET online registration form ची printout Compulsory आहे",
      "Fee payment ची transaction receipt / acknowledgement सोबत असावी",
      "Registration form वरील सर्व details इतर documents शी जुळत असल्याची खात्री करा"
    ]
  },
  {
    id: "man_allotment",
    name: "College Allotment Letter",
    description: "MCC / MahaCET Counselling Authority ने दिलेले official College Allotment Letter — प्रत्येक round नंतर लगेच print करावे.",
    issuer: "MCC / MahaCET Counselling Authority",
    validity: "For Current Round",
    tips: "Round announce झाल्यावर लगेच Allotment Letter print करा. College मध्ये reporting साठी हे document Compulsory आहे.",
    guidelines: [
      "MCC / MahaCET च्या official portal वरून Allotment Letter download करावे",
      "Round announce झाल्यावर लगेच print करणे आवश्यक आहे",
      "College reporting साठी हे document Compulsory आहे",
      "प्रत्येक round साठी नवीन Allotment Letter असेल — latest round चे print करावे"
    ]
  }
];

const getCategoryDocs = (category) => {
  const cat = (category || "").toUpperCase().trim();
  if (cat === "SC" || cat === "ST") {
    return [
      {
        id: "cat_caste",
        name: "Caste Certificate",
        description: "SC/ST साठी State Government ने issued Caste Certificate.",
        issuer: "Competent Authority / SDM",
        validity: "Lifetime",
        tips: "Caste Certificate वरील नावाची spelling 10वी व 12वीच्या marksheet शी तंतोतंत जुळत असल्याची खात्री करा.विद्यार्थ्याचे नाव आडनाव वडिलांचे नाव पुढे मागे असलेले चालेल, फक्त स्पेलिंग मिस्टेक असू नये, व असल्यास दुरुस्त करून घ्यावे",
        guidelines: [
          "SC/ST साठी Maharashtra State Government ने issued Caste Certificate आवश्यक",
          "डॉक्युमेंट वरती बारकोड व व्हेरिफाइड सील असणे आवश्यक",
          "Certificate वरील नाव, जात यातील spelling school records शी जुळणे आवश्यक",
          "Number matching — Caste Certificate Number अन्य documents शी जुळत असावे",
          "Maharashtra State issued certificate असणे आवश्यक आहे"
        ]
      },
      {
        id: "cat_validity",
        name: "Caste Validity Certificate",
        description: "Divisional Caste Scrutiny Committee ने issued Caste Validity Certificate.",
        issuer: "Divisional Caste Scrutiny Committee",
        validity: "Lifetime",
        tips: "Certificate वरील नावाची spelling 10वी व 12वीच्या marksheet शी तंतोतंत जुळत असल्याची खात्री करा.",
        guidelines: [
          "विद्यार्थ्याचे नाव आडनाव वडिलांचे नाव पुढे मागे असलेले चालेल, फक्त स्पेलिंग मिस्टेक असू नये, व असल्यास दुरुस्त करून घ्यावे",
          "डॉक्युमेंट वरती बारकोड व व्हेरिफाइड सील असणे आवश्यक",
          "Divisional Caste Scrutiny Committee ने issued Caste Validity Certificate अनिवार्य आहे",
          "डॉक्युमेंट वरती बारकोड व व्हेरिफाइड सील असणे आवश्यक",
          "Caste Certificate Number आणि Validity Certificate Number एकमेकांशी जुळत असणे आवश्यक"
        ]
      },
      {
        id: "cat_central",
        name: "SC/ST Certificate in Central Format",
        description: "AIIMS, JIPMER, AFMC, आणि All India 15% Quota rounds साठी SC/ST Certificate Central Government format मध्ये आवश्यक.",
        issuer: "Tehshil / SDM",
        validity: "Lifetime",
        tips: "Caste Certificate वरील नावाची spelling 10वी व 12वीच्या marksheet शी तंतोतंत जुळत असल्याची खात्री करा.",
        isCentralFormat: true,
        guidelines: [
          "विद्यार्थ्याचे नाव आडनाव वडिलांचे नाव पुढे मागे असलेले चालेल, फक्त स्पेलिंग मिस्टेक असू नये, व असल्यास दुरुस्त करून घ्यावे",
          "डॉक्युमेंट वरती बारकोड व व्हेरिफाइड सील असणे आवश्यक",
          "Tehshil किंवा SDM यांनी issued असणे आवश्यक",
          "State format certificate Central institutes ला valid मानले जात नाही"
        ]
      }
    ];
  }
  if (cat === "EWS") {
    return [
      {
        id: "cat_ews_state",
        name: "EWS Certificate in State Format",
        description: "EWS Certificate 1 April 2026 रोजी किंवा त्यानंतर issued असणे Compulsory आहे.",
        issuer: "Tahsildar / Competent Authority",
        validity: "Valid till 31/03/2027",
        tips: "Caste Certificate वरील नावाची spelling 10वी व 12वीच्या marksheet शी तंतोतंत जुळत असल्याची खात्री करा.",
        guidelines: [
          "विद्यार्थ्याचे आडनाव - नाव - वडिलांचे नाव पुढे मागे असलेले चालेल, फक्त स्पेलिंग मिस्टेक असू नये, व असल्यास दुरुस्त करून घ्यावे",
          "डॉक्युमेंट वरती बारकोड व व्हेरिफाइड सील असणे आवश्यक",
          "EWS Certificate 1 April 2026 रोजी किंवा त्यानंतर issued असणे Compulsory आहे",
          "Certificate मध्ये Financial Year 2025-26 चा स्पष्ट उल्लेख असणे आवश्यक",
          "Tahsildar किंवा Competent Authority यांनी issued असणे आवश्यक"
        ]
      },
      {
        id: "cat_ews_central",
        name: "Central EWS Certificate",
        description: "Central Government format मध्ये EWS Certificate — AIIMS, JIPMER, AFMC, आणि All India 15% Quota rounds साठी आवश्यक.",
        issuer: "DM / SDM / Tehsildar",
        validity: "Issued on or after 01/04/2026",
        tips: "Certificate वरील नावाची spelling 10वी व 12वीच्या marksheet शी तंतोतंत जुळत असल्याची खात्री करा.",
        isCentralFormat: true,
        guidelines: [
          "विद्यार्थ्याचे नाव आडनाव वडिलांचे नाव पुढे मागे असलेले चालेल, फक्त स्पेलिंग मिस्टेक असू नये, व असल्यास दुरुस्त करून घ्यावे",
          "डॉक्युमेंट वरती बारकोड व व्हेरिफाइड सील असणे आवश्यक",
          "01/04/2026 नंतर issued असणे आवश्यक आहे"
        ]
      }
    ];
  }
  if (["OBC", "VJ-DT", "VJ", "DT", "NT", "NT-B", "NT-C", "NT-D", "SBC", "SEBC"].some(prefix => cat.includes(prefix))) {
    return [
      {
        id: "cat_caste",
        name: "Caste Certificate",
        description: "OBC/NT/VJ/SBC साठी Maharashtra State Caste Certificate.",
        issuer: "Competent Authority / Tahsildar",
        validity: "Lifetime",
        tips: "Caste Certificate वरील जात Maharashtra State Reserved List मध्ये असणे आवश्यक आहे.Certificate वरील नावाची spelling 10वी व 12वीच्या marksheet शी तंतोतंत जुळत असल्याची खात्री करा.",
        guidelines: [
          "विद्यार्थ्याचे नाव आडनाव वडिलांचे नाव पुढे मागे असलेले चालेल, फक्त स्पेलिंग मिस्टेक असू नये, व असल्यास दुरुस्त करून घ्यावे",
          "डॉक्युमेंट वरती बारकोड व व्हेरिफाइड सील असणे आवश्यक",
        ]
      },
      {
        id: "cat_validity",
        name: "Caste Validity Certificate",
        description: "Divisional Scrutiny Committee ने issued Caste Validity Certificate.",
        issuer: "Divisional Scrutiny Committee",
        validity: "Lifetime",
        tips: "Certificate वरील नावाची spelling 10वी व 12वीच्या marksheet शी तंतोतंत जुळत असल्याची खात्री करा.",
        guidelines: [
          "Divisional Scrutiny Committee ने issued Caste Validity Certificate आवश्यक",
          "डॉक्युमेंट वरती बारकोड व व्हेरिफाइड सील असणे आवश्यक",
        ]
      },
      {
        id: "cat_ncl",
        name: "Non-Creamy Layer (NCL) Certificate",
        description: "NCL Certificate 31 March 2027 पर्यंत valid असणे आवश्यक आहे.",
        issuer: "Tahsildar / Sub-Divisional Officer",
        validity: "Valid up to 31/03/2027",
        guidelines: [
          "डॉक्युमेंट वरती बारकोड व व्हेरिफाइड सील असणे आवश्यक",
        ]
      },
      {
        id: "cat_obc_central",
        name: "Central OBC-NCL Certificate",
        description: "Central institutes आणि AIQ Medical Admissions साठी Central format मधील OBC-NCL Certificate आवश्यक.",
        issuer: "DM / SDM / competent Central authority",
        validity: "Issued on or after 01/04/2026",
        tips: "Central Government format मध्ये OBC-NCL Certificate — AIIMS, JIPMER, AFMC, आणि All India 15% Quota rounds साठी आवश्यक.",
        isCentralFormat: true,
        guidelines: [
          "विद्यार्थ्याचे नाव आडनाव वडिलांचे नाव पुढे मागे असलेले चालेल, फक्त स्पेलिंग मिस्टेक असू नये, व असल्यास दुरुस्त करून घ्यावे",
          "डॉक्युमेंट वरती बारकोड व व्हेरिफाइड सील असणे आवश्यक",
          "01/04/2026 रोजी किंवा त्यानंतर issued असणे आवश्यक आहे",
        ]
      }
    ];
  }
  return [];
};

const getSpecialReservationDocs = (reservationString) => {
  const reservations = (reservationString || "")
    .split(",")
    .map(r => r.trim().toUpperCase())
    .filter(r => r && r !== "NONE");

  const docs = [];

  if (reservations.includes("DEF 1") || reservations.includes("DEF1")) {
    docs.push({
      id: "res_def1_ex",
      name: "Ex-Serviceman Certificate (For DEFENCE-1 Only)",
      description: "Certificate issued by District Sainik Welfare Board.",
      issuer: "District Sainik Welfare Officer",
      validity: "Lifetime",
      tips: "Must specify the parent's retirement status and discharge book registry."
    });
    docs.push({
      id: "res_def_dom",
      name: "Domicile Certificate of DEFENCE Person",
      description: "Parent's active military domicile status.",
      issuer: "Competent Tahsildar",
      validity: "Lifetime",
      tips: "Verifies the home town state matching state quota eligibility."
    });
  }
  if (reservations.includes("DEF 2") || reservations.includes("DEF2")) {
    docs.push({
      id: "res_def2_act",
      name: "Actual Service Certificate (For DEFENCE-2 Only)",
      description: "Certificate verifying active serving defense status.",
      issuer: "Commanding Officer of Unit / Regiment",
      validity: "Current Year",
      tips: "Must bear the official military stamp and record date."
    });
    docs.push({
      id: "res_def_dom",
      name: "Domicile Certificate of DEFENCE Person",
      description: "Parent's defense service domicile certificate.",
      issuer: "Competent Tahsildar",
      validity: "Lifetime",
      tips: "Must prove parent is domiciled in Maharashtra."
    });
  }
  if (reservations.includes("DEF 3") || reservations.includes("DEF3")) {
    docs.push({
      id: "res_def3_act",
      name: "Actual Service Certificate (For DEFENCE-3 Only)",
      description: "Active military serving certificate.",
      issuer: "Commanding Officer of Unit",
      validity: "Current Year",
      tips: "Required to claim DEF-3 quota for active personnel posted out of state."
    });
    docs.push({
      id: "res_def_dom",
      name: "Domicile Certificate of DEFENCE Person",
      description: "Parent's defense service domicile certificate.",
      issuer: "Competent Tahsildar",
      validity: "Lifetime",
      tips: "Parent's home state verification."
    });
    docs.push({
      id: "res_def3_tc",
      name: "Transfer Certificate of Defence Parent",
      description: "Transfer order document.",
      issuer: "OC Unit / Army HQ",
      validity: "Current Year",
      tips: "Proves parent has been transferred to Maharashtra state borders."
    });
  }

  if (reservations.includes("HILLY AREA") || reservations.includes("HILLY")) {
    docs.push({
      id: "res_hilly_cert",
      name: "Hilly Area Certificate of Student",
      description: "Resident proof of Hilly Area region.",
      issuer: "Competent Tahsildar / SDM",
      validity: "Lifetime",
      tips: "Tahsildar must certify that the student lives in a declared hilly region."
    });
    docs.push({
      id: "res_hilly_dom",
      name: "Parents Domicile Certificate from Hilly Area",
      description: "Parent's resident domicile.",
      issuer: "Competent Tahsildar",
      validity: "Lifetime",
      tips: "Must specify parents live in the same hilly taluka."
    });
    docs.push({
      id: "res_hilly_proof",
      name: "Candidates 10th/12th from Hilly Village/Taluka Proof",
      description: "Schooling certificates proving high school completion from hilly area.",
      issuer: "Headmaster of School attended",
      validity: "Lifetime",
      tips: "Academic certificates showing candidate attended school located in the hilly taluka."
    });
  }

  if (reservations.includes("MKB")) {
    docs.push({
      id: "res_mkb_border",
      name: "Disputed Border Area Certificate",
      description: "Residency proof in declared border area.",
      issuer: "Competent District Collector / Tahsildar",
      validity: "Lifetime",
      tips: "Candidate must prove residence in one of the disputed border area villages."
    });
    docs.push({
      id: "res_mkb_lang",
      name: "Mother Tongue (Marathi) Certificate",
      description: "Official school or legal declaration of Marathi mother tongue.",
      issuer: "Headmaster of School / Notary",
      validity: "Lifetime",
      tips: "School records or school leaving certificate must explicitly state mother tongue is Marathi."
    });
    docs.push({
      id: "res_mkb_proof",
      name: "SSC/HSC Disputed Border Area Proof",
      description: "Board certificates.",
      issuer: "Disputed Area High School Authority",
      validity: "Lifetime",
      tips: "SSC or HSC must have been cleared from institutions inside the border area boundary."
    });
  }

  if (reservations.includes("PWD")) {
    docs.push({
      id: "res_pwd_cert",
      name: "Certificate of Disability",
      description: "Physical handicap disability scorecard.",
      issuer: "Designated Central Medical Board (e.g. Grant Medical College, Mumbai)",
      validity: "Current Year",
      tips: "Must be issued by one of the 15+ centers authorized by MCI/NMC."
    });
  }

  if (reservations.includes("ORPHAN")) {
    docs.push({
      id: "res_orphan_cert",
      name: "Orphan Certificate",
      description: "Official government orphan certificate.",
      issuer: "Women & Child Welfare Dept. Govt. of Maharashtra",
      validity: "Lifetime",
      tips: "Grants 1% parallel reservation in state quotas."
    });
  }

  // Deduplicate
  const uniqueDocs = [];
  const seenIds = new Set();
  for (const doc of docs) {
    if (!seenIds.has(doc.id)) {
      seenIds.add(doc.id);
      uniqueDocs.push(doc);
    }
  }
  return uniqueDocs;
};

const CONCESSION_DOCS = [
  {
    id: "con_income",
    name: "Family Income Certificate / Form No-16",
    description: "Income certificate proving family annual income limits.",
    issuer: "Competent Tahsildar / Employer",
    validity: "Valid up to 31st March 2027",
    tips: "Must show annual income of family is below ₹8 Lakhs for EBC/EWS concessions."
  },
  {
    id: "con_bank",
    name: "Aadhaar Number Linked Bank Account Passbook",
    description: "Student's bank savings passbook copy.",
    issuer: "Nationalized Bank branch",
    validity: "Lifetime",
    tips: "Account must be strictly in the student's name and seeded with Aadhaar."
  },
  {
    id: "con_aadhar_pan",
    name: "Student & Parents - Aadhaar & PAN Card",
    description: "Financial identity proofs.",
    issuer: "Income Tax Department / UIDAI",
    validity: "Lifetime",
    tips: "Required for registration in scholarship portals (e.g., MahaDBT)."
  },
  {
    id: "con_ration",
    name: "Ration Card",
    description: "Official household ration card copy.",
    issuer: "District Food and Civil Supplies Office",
    validity: "Current",
    tips: "Required to verify number of siblings to claim specific fee waivers."
  }
];

export default function DocumentChecker() {
  const navigate = useNavigate();
  const { user, isProfileComplete } = usePublicAuth();

  const userCategory = user?.category || "GEN";
  const userReservations = user?.special_reservation || "None";
  const userDistrict = user?.district || "Maharashtra";
  const userStudentType = user?.student_type || "";

  // Compile full dynamic documents list
  const categoryDocs = getCategoryDocs(userCategory);
  const reservationDocs = getSpecialReservationDocs(userReservations);

  // Filter out Gap Affidavit for fresher students
  const filteredMandatoryDocs = userStudentType === "FRESHER"
    ? MANDATORY_DOCS.filter(d => d.id !== "man_gap")
    : MANDATORY_DOCS;

  const allDocsList = [
    ...filteredMandatoryDocs.map(d => ({ ...d, section: "Mandatory Core Documents" })),
    ...categoryDocs.map(d => ({ ...d, section: `Category Documents (${userCategory})` })),
    ...reservationDocs.map(d => ({ ...d, section: "Special Reservation Documents" })),
    ...CONCESSION_DOCS.map(d => ({ ...d, section: "Fee Concessions Helper List" }))
  ];

  // Local storage state keyed by unique student ID or phone to persist checks
  const storageKey = `ame_doc_check_v3_${user?.phone || "guest"}`;

  // 3-state checkboxes: UNCHECKED (undefined / "UNCHECKED"), IN_PROGRESS, VERIFIED
  const [checkedDocs, setCheckedDocs] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [activeModalDoc, setActiveModalDoc] = useState(null);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [filterMissing, setFilterMissing] = useState(false);
  const pdfRef = React.useRef(null);
  const [openSections, setOpenSections] = useState({
    "Mandatory Core Documents": false,
    [`Category Documents (${userCategory})`]: false,
    "Special Reservation Documents": false,
    "Fee Concessions Helper List": false
  });

  // React Confetti Micro-interaction Particle Engine
  const [confettiActive, setConfettiActive] = useState(false);
  const [confettiParticles, setConfettiParticles] = useState([]);

  const triggerConfetti = () => {
    setConfettiActive(true);
    const colors = ["#FF6B35", "#00C896", "#38bdf8", "#ec4899", "#eab308"];
    const particles = Array.from({ length: 45 }).map((_, idx) => ({
      id: idx,
      x: Math.random() * 100, // screen width percentage
      y: -10 - Math.random() * 20, // offset top
      size: Math.random() * 8 + 6,
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: Math.random() * 1.5,
      rotation: Math.random() * 360
    }));
    setConfettiParticles(particles);
    setTimeout(() => {
      setConfettiActive(false);
      setConfettiParticles([]);
    }, 4500);
  };

  const [prevSectionStatus, setPrevSectionStatus] = useState({});

  useEffect(() => {
    // Dynamically calculate which sections are active and completed
    const activeSections = [
      { name: "Mandatory Core Documents", docs: MANDATORY_DOCS },
      { name: `Category Documents (${userCategory})`, docs: categoryDocs },
      { name: "Special Reservation Documents", docs: reservationDocs },
      { name: "Fee Concessions Helper List", docs: CONCESSION_DOCS }
    ].filter(s => s.docs.length > 0);

    const currentStatus = {};
    let triggered = false;

    activeSections.forEach(sec => {
      const sectionCheckedCount = sec.docs.filter(d => checkedDocs[d.id] === "VERIFIED").length;
      const sectionTotalCount = sec.docs.length;
      const isDone = sectionCheckedCount === sectionTotalCount && sectionTotalCount > 0;

      currentStatus[sec.name] = isDone;

      // If it was previously NOT done (or was undefined/false) and is NOW done, trigger confetti!
      if (isDone && prevSectionStatus[sec.name] === false) {
        triggered = true;
      }
    });

    setPrevSectionStatus(prev => {
      if (Object.keys(prev).length === 0) {
        return currentStatus;
      }
      return { ...prev, ...currentStatus };
    });

    if (triggered) {
      triggerConfetti();
    }
  }, [checkedDocs]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(checkedDocs));
  }, [checkedDocs, storageKey]);

  const setDocState = (docId, nextState) => {
    setCheckedDocs(prev => {
      const updated = { ...prev, [docId]: nextState };
      const docName = allDocsList.find(d => d.id === docId)?.name || "Document";
      if (nextState === "VERIFIED") {
        toast.success(`Prepared: ${docName}`);
      } else if (nextState === "IN_PROGRESS") {
        toast.warning(`In Progress: ${docName}`);
      } else {
        toast.info(`Reset: ${docName}`);
      }
      return updated;
    });
  };

  // Cycle states: UNCHECKED -> IN_PROGRESS -> VERIFIED -> UNCHECKED
  const cycleDocState = (docId) => {
    setCheckedDocs(prev => {
      const current = prev[docId] || "UNCHECKED";
      let nextState;
      let toastMsg = "";

      if (current === "UNCHECKED") {
        nextState = "IN_PROGRESS";
        toastMsg = "In Progress";
      } else if (current === "IN_PROGRESS") {
        nextState = "VERIFIED";
        toastMsg = "Verified ✓";
      } else {
        nextState = "UNCHECKED";
        toastMsg = "Unchecked";
      }

      const updated = { ...prev, [docId]: nextState };
      const docName = allDocsList.find(d => d.id === docId)?.name || "Document";

      if (nextState === "VERIFIED") {
        toast.success(`Prepared: ${docName}`);
      } else if (nextState === "IN_PROGRESS") {
        toast.warning(`In Progress: ${docName}`);
      } else {
        toast.info(`Reset: ${docName}`);
      }

      return updated;
    });
  };

  const toggleSection = (sectionName) => {
    setOpenSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  const toggleExpand = (docId) => {
    setExpandedDoc(prev => (prev === docId ? null : docId));
  };

  const handlePrint = () => {
    toast.success("Generating personalized PDF checklist...");
    const element = pdfRef.current;
    if (!element) return;
    element.style.display = 'block';

    const opt = {
      margin: [0.4, 0.4, 0.4, 0.4],
      filename: `AME_Checklist_${user?.full_name?.replace(/\s+/g, '_') || 'Student'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    };

    html2pdf().set(opt).from(element).save().then(() => {
      element.style.display = 'none';
    });
  };

  const isStateReady = !!user;

  if (!isStateReady) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <AlertCircle className="w-12 h-12 text-[#FF6B35] animate-pulse mb-4" />
        <p className="text-lg font-semibold">Loading profile information...</p>
      </div>
    );
  }

  // Filter based on "What's Missing?" CTA
  const displayedDocs = filterMissing
    ? allDocsList.filter(d => (checkedDocs[d.id] || "UNCHECKED") !== "VERIFIED")
    : allDocsList;

  // Stats calculation
  const totalCount = allDocsList.length;
  const verifiedCount = allDocsList.filter(d => checkedDocs[d.id] === "VERIFIED").length;
  const progressPercent = totalCount > 0 ? Math.round((verifiedCount / totalCount) * 100) : 0;
  const docsLeft = totalCount - verifiedCount;

  // Grouping documents by section
  const sections = {
    "Mandatory Core Documents": displayedDocs.filter(d => d.section === "Mandatory Core Documents"),
    [`Category Documents (${userCategory})`]: displayedDocs.filter(d => d.section.startsWith("Category Documents")),
    "Special Reservation Documents": displayedDocs.filter(d => d.section === "Special Reservation Documents"),
    "Fee Concessions Helper List": displayedDocs.filter(d => d.section === "Fee Concessions Helper List")
  };

  const sectionMetadata = {
    "Mandatory Core Documents": { icon: "📋", border: "border-l-sky-500/85" },
    [`Category Documents (${userCategory})`]: { icon: "🏷️", border: "border-l-orange-500/85" },
    "Special Reservation Documents": { icon: "🎖️", border: "border-l-indigo-500/85" },
    "Fee Concessions Helper List": { icon: "💰", border: "border-l-emerald-500/85" }
  };

  return (
    <div
      className="max-w-[1140px] mx-auto px-4 md:px-6 space-y-6 pb-[72px] lg:pb-8 text-[#f8fafc] w-full"
      style={{ fontFamily: "'Inter', 'Geist', sans-serif", background: "linear-gradient(to bottom, #0d1117 0%, #111522 50%, #1a1f2e 100%)" }}
      data-testid="document-checker-container"
    >
      {/* ─── Hidden PDF Template ─────────────────────────────────────────── */}
      <div style={{ display: 'none' }}>
        <div ref={pdfRef} style={{ background: 'white', color: 'black', fontFamily: 'sans-serif', padding: '25px', maxWidth: '800px', margin: '0 auto' }}>
          {/* Condensed Header & Student Profile Box */}
          <div style={{ marginBottom: '20px' }}>
            {/* Top Branding Row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '2px solid #1e3a8a', paddingBottom: '10px', marginBottom: '12px' }}>
              <div>
                <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: '#1e3a8a', margin: 0, letterSpacing: '-0.5px' }}>Admissions Made Easy Portal</h1>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0 0', fontWeight: '600' }}>Official NEET-UG 2026 Document Checklist</p>
              </div>
              <div style={{ textAlign: 'right', fontSize: '10px', color: '#64748b', fontWeight: '600' }}>
                Generated on: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </div>
            </div>

            {/* Profile Row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px 16px' }}>
              <div><strong style={{ color: '#64748b', fontSize: '9px', textTransform: 'uppercase' }}>Name</strong><br /><span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a' }}>{user?.full_name || "Student"}</span></div>
              <div><strong style={{ color: '#64748b', fontSize: '9px', textTransform: 'uppercase' }}>Phone</strong><br /><span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a' }}>{user?.phone || "N/A"}</span></div>
              <div><strong style={{ color: '#64748b', fontSize: '9px', textTransform: 'uppercase' }}>Category</strong><br /><span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a' }}>{userCategory}</span></div>
              <div><strong style={{ color: '#64748b', fontSize: '9px', textTransform: 'uppercase' }}>Special Res.</strong><br /><span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a' }}>{userReservations}</span></div>
              <div><strong style={{ color: '#64748b', fontSize: '9px', textTransform: 'uppercase' }}>Student Type</strong><br /><span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a' }}>{userStudentType || "N/A"}</span></div>
              <div><strong style={{ color: '#64748b', fontSize: '9px', textTransform: 'uppercase' }}>Domicile</strong><br /><span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a' }}>{userDistrict}</span></div>
            </div>
          </div>

          {/* Tables */}
          {Object.entries(sections).map(([sectionName, docs]) => {
            if (docs.length === 0) return null;
            return (
              <div key={sectionName} style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px', backgroundColor: '#f1f5f9', padding: '8px 12px', borderRadius: '4px' }}>
                  {sectionName}
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                      <th style={{ padding: '8px', color: '#475569', fontWeight: 'bold', textAlign: 'center', width: '40px' }}>Sr.</th>
                      <th style={{ padding: '8px', color: '#475569', fontWeight: 'bold', textAlign: 'left', width: '220px' }}>Document Name</th>
                      <th style={{ padding: '8px', color: '#475569', fontWeight: 'bold', textAlign: 'left' }}>Important Note / Guidelines</th>
                      <th style={{ padding: '8px', color: '#475569', fontWeight: 'bold', textAlign: 'center', width: '50px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((doc, idx) => (
                      <tr key={doc.id} style={{ borderBottom: '1px solid #e2e8f0', pageBreakInside: 'avoid' }}>
                        <td style={{ padding: '8px', textAlign: 'center', color: '#64748b', fontWeight: '500' }}>{idx + 1}</td>
                        <td style={{ padding: '8px', fontWeight: 'bold', color: '#0f172a', lineHeight: '1.4' }}>{doc.name}</td>
                        <td style={{ padding: '8px', color: '#475569', lineHeight: '1.4' }}>{doc.tips || doc.description || "Required, If Eligible "}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <div style={{ width: '14px', height: '14px', border: '1.5px solid #94a3b8', margin: '0 auto', borderRadius: '2px' }}></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* Footer Signatures */}
          <div style={{ marginTop: '30px', pageBreakInside: 'avoid' }}>
            <p style={{ fontSize: '11px', fontStyle: 'italic', color: '#ef4444', marginBottom: '40px', fontWeight: '500' }}>
              <strong>Note:</strong> Please keep 3 sets of attested photocopies ready. The allotted college will retain all original files during physical admission rounds.
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 20px' }}>
              <div style={{ width: '180px', borderTop: '1px solid #64748b', paddingTop: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#334155' }}>
                Student Signature
              </div>
              <div style={{ width: '180px', borderTop: '1px solid #64748b', paddingTop: '8px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#334155' }}>
                Parent / Guardian Signature
              </div>
            </div>
          </div>

          {/* Branch Details */}
          <div style={{ marginTop: '40px', borderTop: '1px solid #cbd5e1', paddingTop: '16px', pageBreakInside: 'avoid' }}>
            <h4 style={{ fontSize: '11px', fontWeight: 'bold', color: '#1e3a8a', marginBottom: '12px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Admissions Made Easy Branches</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', fontSize: '9px', color: '#475569' }}>
              {BRANCH_DETAILS.map(branch => (
                <div key={branch.city} style={{ background: '#f8fafc', padding: '6px 10px', borderRadius: '4px', border: '1px solid #e2e8f0', fontWeight: '500' }}>
                  <strong style={{ color: '#0f172a' }}>{branch.city}:</strong> {branch.phones}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .print-full-width { width: 100% !important; max-width: 100% !important; border: none !important; }
        }
        @keyframes pulse-light {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.9; transform: scale(1.02); }
        }
        .progress-glow {
          filter: drop-shadow(0 0 12px rgba(0, 200, 150, 0.4));
        }
        .luxury-glass-card {
          background: rgba(18, 22, 32, 0.6) !important;
          backdrop-filter: blur(16px) !important;
          border: 1px solid rgba(255, 255, 255, 0.06) !important;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4) !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .luxury-glass-card:hover {
          border-color: rgba(0, 200, 150, 0.25) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 20px 40px rgba(0, 200, 150, 0.08) !important;
        }
        .shimmer-btn {
          position: relative;
          overflow: hidden;
        }
        .shimmer-btn::after {
          content: "";
          position: absolute;
          top: 0;
          left: -60%;
          width: 30%;
          height: 100%;
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.25) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          transform: skewX(-25deg);
          transition: all 0.75s ease;
        }
        .shimmer-btn:hover::after {
          left: 140%;
          transition: all 0.75s ease;
        }
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-none {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes confetti-fall {
          0% {
            transform: translateY(-50px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(105vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti-fall {
          animation-name: confetti-fall;
          animation-timing-function: linear;
          animation-fill-mode: forwards;
        }
        @keyframes expandCardIn {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-expand-card-in {
          animation: expandCardIn 250ms ease-out forwards;
        }
        @keyframes statusPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0,200,150,0.4); }
          50% { box-shadow: 0 0 0 6px rgba(0,200,150,0); }
        }
        .animate-status-pulse {
          animation: statusPulse 2s ease-in-out infinite;
        }
        @keyframes spinSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spinSlow 2s linear infinite;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in-backdrop {
          animation: fadeIn 200ms ease-out forwards;
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes scaleUp {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }

      `}</style>

      {/* ─── Confetti Particle Burst ─────────────────────────────────────────── */}
      {confettiActive && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden no-print">
          {confettiParticles.map(p => (
            <div
              key={p.id}
              className="absolute animate-confetti-fall rounded-sm"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: p.size,
                height: p.size * 1.5,
                backgroundColor: p.color,
                transform: `rotate(${p.rotation}deg)`,
                animationDelay: `${p.delay}s`,
                animationDuration: `${2.5 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>
      )}

      {/* ─── Sticky Banner for Remaining Documents ────────────────────────────── */}
      {docsLeft > 0 && docsLeft <= 5 && (
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-[#FF6B35]/30 p-3.5 rounded-2xl flex items-center justify-between shadow-xl animate-fade-in no-print">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 bg-[#FF6B35] rounded-full animate-ping"></span>
            <span className="text-sm font-bold text-slate-200">
              Almost ready for counselling! <span className="text-[#FF6B35] font-extrabold">{docsLeft}</span> document{docsLeft > 1 ? 's' : ''} left.
            </span>
          </div>
          <button
            onClick={() => setFilterMissing(true)}
            className="text-xs font-bold text-[#FF6B35] bg-[#FF6B35]/15 border border-[#FF6B35]/25 px-3.5 py-2 rounded-xl hover:bg-[#FF6B35]/25 transition-all"
          >
            Show Remaining Only
          </button>
        </div>
      )}

      {/* ─── Two-Column Responsive Layout Grid ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start print-full-width">

        {/* Left Column (Header & Accordions) */}
        <div className="lg:col-span-2 space-y-6">

          {/* Main Info Card */}
          <div className="bg-[#13151b] p-[24px_28px] rounded-2xl border border-white/5 flex flex-col justify-between">
            <div className="flex flex-col gap-[10px] pb-3 border-b border-white/[0.06] mb-3">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-xl bg-[#ff6b35]/10 border border-[#ff6b35]/20 flex items-center justify-center text-[#ff6b35] flex-shrink-0">
                  <ClipboardCheck className="w-5.5 h-5.5" />
                </div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight text-white">
                  Document Checker
                </h1>
              </div>
              <p className="text-slate-400 text-xs md:text-sm leading-[1.7] max-w-[580px]">
                Complete your checklist, verify Central Formats, and organize your files ready for physical reporting. Tapping a checkbox cycles through status states.
              </p>
            </div>

            {/* Integrated Profile Context Bar (Mobile Horizontal Swipe-Scroll) */}
            <div className="flex flex-nowrap items-center gap-[10px] pt-[12px] overflow-x-auto scrollbar-none pb-1.5 select-none -mx-2 px-2">
              {/* Category Badge */}
              <div className="flex items-center gap-2 bg-[#1E2D3D]/40 px-3.5 py-1.5 rounded-full border border-white/5 flex-shrink-0">
                <span className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">CAT:</span>
                <span
                  className="font-black text-[10.5px] px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: userCategory !== "GEN" ? "rgba(245, 158, 11, 0.15)" : "rgba(255,255,255,0.05)",
                    color: userCategory !== "GEN" ? "#F59E0B" : "#94a3b8"
                  }}
                >
                  {userCategory}
                </span>
              </div>

              {/* Reservation Badge */}
              <div className="flex items-center gap-2 bg-[#1E2D3D]/40 px-3.5 py-1.5 rounded-full border border-white/5 flex-shrink-0">
                <span className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Special Reservation:</span>
                <span
                  className="font-black text-[10.5px] px-2 py-0.5 rounded-full truncate max-w-[140px]"
                  style={{
                    backgroundColor: userReservations !== "None" ? "rgba(59, 130, 246, 0.15)" : "rgba(255,255,255,0.05)",
                    color: userReservations !== "None" ? "#3B82F6" : "#94a3b8"
                  }}
                >
                  {userReservations}
                </span>
              </div>


            </div>
          </div>

          {/* Top Mobile Download Button */}
          <button
            onClick={handlePrint}
            className="md:hidden shimmer-btn w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 font-black text-sm transition-all hover:shadow-[0_0_15px_rgba(0,200,150,0.3)] border border-[#00C896]/20 shadow-md select-none no-print"
          >
            <Download className="w-4 h-4 text-slate-950 stroke-[3]" />
            Download Checklist PDF
          </button>

          {/* Alert if Profile is incomplete */}
          {!isProfileComplete && (
            <div className="bg-amber-500/10 border border-[#FF6B35]/30 p-4.5 rounded-2xl flex items-start gap-3.5 no-print">
              <Info className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-white">Incomplete Profile Details</h4>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  Your profile is not fully completed. Complete your details (Category, Reservations) in the Profile tab to compile your dynamic caste and defense certificates automatically.
                </p>
              </div>
            </div>
          )}

          {/* Document List Accordions */}
          <div className="space-y-4 print-full-width">
            {Object.entries(sections).map(([sectionName, docs]) => {
              if (docs.length === 0) return null;

              const sectionCheckedCount = docs.filter(d => checkedDocs[d.id] === "VERIFIED").length;
              const sectionTotalCount = docs.length;
              const isFullyDone = sectionCheckedCount === sectionTotalCount;
              const config = sectionMetadata[sectionName] || { icon: "📋", border: "border-l-purple-500/80" };
              const isOpen = !!openSections[sectionName];

              return (
                <div
                  key={sectionName}
                  className={`luxury-glass-card rounded-2xl print-full-width border-l-4 ${config.border}`}
                >
                  {/* Collapsible Section Header */}
                  <div
                    onClick={() => toggleSection(sectionName)}
                    className="flex items-center justify-between p-5 bg-[#1E2D3D]/10 rounded-t-2xl cursor-pointer hover:bg-[#1E2D3D]/20 transition-all select-none border-b border-white/5"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-slate-800/80 text-xl border border-white/5 shadow-inner">
                        {config.icon}
                      </div>
                      <div>
                        <h3 className="font-extrabold text-sm md:text-base text-white tracking-wide">{sectionName}</h3>
                        <p className="text-[11px] text-slate-500 font-semibold mt-0.5">{sectionTotalCount} document{sectionTotalCount !== 1 ? 's' : ''} — tap any row to view guidelines</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 no-print">
                      {isFullyDone && (
                        <span className="text-[10px] font-black text-[#00C896] bg-[#00C896]/15 border border-[#00C896]/20 px-2.5 py-0.5 rounded-full tracking-wider uppercase select-none">
                          Completed
                        </span>
                      )}
                      <button className="text-slate-400 p-1">
                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Section Documents List */}
                  {isOpen && (
                    <div className="divide-y divide-white/5 animate-slide-down">
                      {docs.map((doc, idx) => {
                        const isExpanded = expandedDoc === doc.id;

                        return (
                          <div
                            key={doc.id}
                            className="flex flex-col transition-all duration-200"
                            data-testid={`document-item-${doc.id}`}
                            style={{
                              borderLeft: `4px solid rgba(56,189,248,0.7)`,
                              ...(isExpanded ? { boxShadow: `0 -1px 20px rgba(56,189,248,0.07)` } : {})
                            }}
                          >
                            {/* ─── Title Row ─────────────────────────────────────── */}
                            <div
                              className="flex items-center justify-between p-3.5 sm:p-4 gap-3 cursor-pointer hover:bg-white/[0.025] transition-all"
                              onClick={() => toggleExpand(doc.id)}
                            >
                              <div className="flex items-center gap-3 sm:gap-3.5 flex-1 min-w-0" style={{ minHeight: 48 }}>
                                {/* Numbered Badge */}
                                <div
                                  className="flex items-center justify-center rounded-xl flex-shrink-0 text-[11px] font-black text-sky-400"
                                  style={{
                                    width: 32,
                                    height: 32,
                                    background: 'rgba(56,189,248,0.1)',
                                    border: '1px solid rgba(56,189,248,0.2)'
                                  }}
                                >
                                  {idx + 1}
                                </div>

                                <div className="min-w-0 flex flex-col gap-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p
                                      className="text-[15px] lg:text-[16px] font-semibold text-slate-100 line-clamp-2"
                                      style={{ letterSpacing: '-0.01em', lineHeight: 1.4 }}
                                    >
                                      {doc.name}
                                    </p>
                                  </div>
                                  {/* Central Format pill */}
                                  {doc.isCentralFormat && (
                                    <div className="flex items-center gap-1 text-[9px] font-black text-[#FF6B35] bg-[#FF6B35]/15 border border-[#FF6B35]/25 px-1.5 py-0.5 rounded uppercase select-none w-fit">
                                      <ShieldAlert className="w-2.5 h-2.5" />
                                      Central Format Required
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Chevron */}
                              <div
                                className="p-1 sm:p-2 rounded-lg text-slate-400 flex-shrink-0"
                                style={{ minWidth: 32, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                <div className="transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                  <ChevronDown className="w-5 h-5" />
                                </div>
                              </div>
                            </div>

                            {/* ─── Expandable Meta Panel ─────────────────────────── */}
                            {isExpanded && (
                              <div
                                className="animate-expand-card-in p-4 sm:p-5 lg:px-[18px] text-xs text-slate-400 flex flex-col gap-3 sm:gap-[14px]"
                                style={{
                                  background: 'linear-gradient(145deg, #141b2d, #0f1521)',
                                  borderRadius: '0 0 16px 16px',
                                  border: '1px solid rgba(255,255,255,0.07)',
                                  borderTop: 'none'
                                }}
                              >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                  {/* [1,1] Issuing Authority */}
                                  <div
                                    className="flex flex-col sm:flex-row items-start sm:items-start gap-2 sm:gap-3 cursor-default transition-all duration-200"
                                    style={{
                                      background: 'rgba(59,130,246,0.07)',
                                      border: '1px solid rgba(59,130,246,0.12)',
                                      borderRadius: '10px',
                                      padding: '10px 12px',
                                      borderLeftWidth: '3px',
                                      borderLeftColor: '#3b82f6'
                                    }}
                                  >
                                    <div className="flex items-center gap-1.5 mb-1 sm:mb-0">
                                      <Building2 className="w-[16px] h-[16px] sm:w-[18px] sm:h-[18px] text-blue-400 flex-shrink-0" />
                                      <span
                                        className="font-bold uppercase block sm:hidden"
                                        style={{ fontSize: '9px', letterSpacing: '0.08em', color: 'rgba(59,130,246,0.8)' }}
                                      >Issuing Authority</span>
                                    </div>
                                    <div className="min-w-0">
                                      <span
                                        className="font-bold uppercase hidden sm:block mb-1"
                                        style={{ fontSize: '10px', letterSpacing: '0.08em', color: 'rgba(148,163,184,0.7)' }}
                                      >Issuing Authority</span>
                                      <span
                                        className="block mt-0.5"
                                        style={{ fontSize: '13.5px', fontWeight: 600, lineHeight: '1.5', color: '#fff' }}
                                      >
                                        {doc.issuer || "Competent Authority"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* [1,2] Required Validity */}
                                  <div
                                    className="flex flex-col sm:flex-row items-start sm:items-start gap-2 sm:gap-3 cursor-default transition-all duration-200"
                                    style={{
                                      background: 'rgba(34,197,94,0.07)',
                                      border: '1px solid rgba(34,197,94,0.12)',
                                      borderRadius: '10px',
                                      padding: '10px 12px',
                                      borderLeftWidth: '3px',
                                      borderLeftColor: '#22c55e'
                                    }}
                                  >
                                    <div className="flex items-center gap-1.5 mb-1 sm:mb-0">
                                      <ShieldCheck className="w-[16px] h-[16px] sm:w-[18px] sm:h-[18px] text-emerald-400 flex-shrink-0" />
                                      <span
                                        className="font-bold uppercase block sm:hidden"
                                        style={{ fontSize: '9px', letterSpacing: '0.08em', color: 'rgba(34,197,94,0.8)' }}
                                      >Required Validity</span>
                                    </div>
                                    <div className="min-w-0">
                                      <span
                                        className="font-bold uppercase hidden sm:block mb-1"
                                        style={{ fontSize: '10px', letterSpacing: '0.08em', color: 'rgba(148,163,184,0.7)' }}
                                      >Required Validity</span>
                                      <span
                                        className="block mt-0.5"
                                        style={{ fontSize: '13.5px', fontWeight: 600, lineHeight: '1.5', color: '#fff' }}
                                      >
                                        {doc.validity || "Lifetime"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* [2,1] Pro Tip (Spans across both columns if present) */}
                                  {doc.tips && (
                                    <div
                                      className="flex flex-col sm:flex-row items-start sm:items-start gap-2 sm:gap-3 cursor-default transition-all duration-200 sm:col-span-2"
                                      style={{
                                        background: 'rgba(234,179,8,0.06)',
                                        border: '1px solid rgba(234,179,8,0.18)',
                                        borderRadius: '10px',
                                        padding: '10px 12px',
                                        borderLeftWidth: '3px',
                                        borderLeftColor: '#eab308'
                                      }}
                                    >
                                      <div className="flex items-center gap-1.5 mb-1 sm:mb-0">
                                        <Lightbulb className="w-[16px] h-[16px] sm:w-[18px] sm:h-[18px] text-amber-400 flex-shrink-0" />
                                        <span
                                          className="font-bold uppercase block sm:hidden"
                                          style={{ fontSize: '9px', letterSpacing: '0.12em', color: '#eab308' }}
                                        >Pro Tip</span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <span
                                          className="font-bold uppercase hidden sm:block mb-1"
                                          style={{ fontSize: '10px', letterSpacing: '0.12em', color: '#eab308' }}
                                        >Pro Tip</span>
                                        <p style={{ fontSize: '13.5px', fontWeight: 600, lineHeight: '1.5', color: 'rgba(255,255,255,0.85)' }}>{doc.tips}</p>
                                      </div>
                                    </div>
                                  )}
                                </div>


                                {/* Central Format Block Warning */}
                                {doc.isCentralFormat && (
                                  <div
                                    className="flex items-start gap-2.5 sm:gap-3"
                                    style={{
                                      background: 'rgba(239,68,68,0.05)',
                                      border: '1px solid rgba(239,68,68,0.18)',
                                      borderLeft: '3px solid #ef4444',
                                      borderRadius: '10px',
                                      padding: '10px 12px'
                                    }}
                                  >
                                    <ShieldAlert className="w-[16px] h-[16px] sm:w-[18px] sm:h-[18px] text-red-400 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                      <span className="text-[9px] font-bold text-red-400 uppercase block mb-0.5" style={{ letterSpacing: '0.1em' }}>Central Format Mandated</span>
                                      <p className="text-[11.5px] leading-relaxed font-medium text-slate-300">
                                        <strong>Why?</strong> AIIMS, JIPMER, Deemed Universities, and all 15% All India Quota rounds strictly refuse state-level formats.
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* ─── Guidelines & Requirements Block ────────── */}
                                {doc.guidelines && doc.guidelines.length > 0 && (
                                  <div
                                    style={{
                                      background: 'rgba(56,189,248,0.05)',
                                      border: '1px solid rgba(56,189,248,0.15)',
                                      borderLeft: '3px solid #38bdf8',
                                      borderRadius: '10px',
                                      padding: '12px 14px'
                                    }}
                                  >
                                    <div className="flex items-center gap-2 mb-2.5">
                                      <Lightbulb className="w-4 h-4 text-sky-400 flex-shrink-0" />
                                      <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.1em', color: '#38bdf8', textTransform: 'uppercase' }}>Guidelines &amp; Requirements</span>
                                    </div>
                                    <ul className="space-y-2">
                                      {doc.guidelines.map((rule, rIdx) => (
                                        <li key={rIdx} className="flex items-start gap-2">
                                          <span style={{ color: '#38bdf8', fontSize: '13px', lineHeight: 1.6, flexShrink: 0, marginTop: '1px' }}>▸</span>
                                          <span style={{ fontSize: '13px', fontWeight: 500, lineHeight: 1.6, color: 'rgba(226,232,240,0.9)' }}>{rule}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {/* Hint Strip */}
                                <div
                                  className="flex items-start gap-2.5 mt-1"
                                  style={{
                                    background: 'rgba(244,63,94,0.08)',
                                    border: '1px solid rgba(244,63,94,0.18)',
                                    borderLeft: '3px solid #f43f5e',
                                    borderRadius: '10px',
                                    padding: '10px 14px'
                                  }}
                                >
                                  <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#fb7185' }} />
                                  <p style={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.55, color: 'rgba(255,255,255,0.88)' }}>
                                    {doc.description}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop-only Extras (Quick Tips, Legend, and Ghost Download CTA) */}
          <div className="hidden lg:block space-y-4 pt-4 no-print">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Quick Tips Card */}
              <div
                className="p-5 flex flex-col gap-3"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px" }}
              >
                <div className="flex items-center gap-2 text-amber-400">
                  <Info className="w-4.5 h-4.5" />
                  <span className="text-xs font-black uppercase tracking-wider text-slate-300">Quick Tips</span>
                </div>
                <ul className="text-[11px] text-slate-400 space-y-2 list-disc pl-4 font-semibold leading-relaxed">
                  <li>Keep originals + 3 attested photocopies for each document</li>
                  <li>OBC certificate must be issued within the last 1 year</li>
                  <li>Ensure Domicile certificate matches your Akola address</li>
                </ul>
              </div>

              {/* Status Legend Card & Mirror PDF CTA */}
              <div className="flex flex-col justify-between gap-4">
                {/* Status Legend Strip */}
                <div
                  className="p-4 flex flex-col justify-center gap-2"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px" }}
                >
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block">Status Legend</span>
                  <div className="flex items-center gap-3 text-[11px] text-slate-300 font-bold mt-1">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]"></span>Not Ready</span>
                    <span className="text-slate-600 select-none">·</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]"></span>In Progress</span>
                    <span className="text-slate-600 select-none">·</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#00C896] shadow-[0_0_8px_rgba(0,200,150,0.5)]"></span>Ready</span>
                  </div>
                </div>

                {/* Ghost Download Button */}
                <button
                  onClick={handlePrint}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 transition-all text-slate-300 hover:text-white hover:bg-white/5 font-bold text-xs select-none"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px" }}
                >
                  <Download className="w-3.5 h-3.5 text-slate-300" />
                  Download Checklist PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (Sticky Desktop Sidebar / Overall Progress Indicators) */}
        <div className="lg:col-span-1 lg:sticky lg:top-6 space-y-6 no-print">

          {/* Document Summary Card */}
          <div className="bg-[#13151b] p-6 rounded-2xl border border-white/5 flex flex-col items-center text-center gap-4 shadow-lg">
            <span className="text-[10px] font-black text-sky-400 uppercase tracking-widest leading-none select-none">DOCUMENT SUMMARY</span>

            {/* Big Number */}
            <div className="flex flex-col items-center gap-1">
              <span className="text-5xl font-black text-white leading-none">{totalCount}</span>
              <span className="text-[11px] text-slate-400 font-semibold mt-1">Total Documents Required</span>
            </div>

            {/* Per section breakdown */}
            <div className="w-full space-y-2.5 text-left">
              {[
                { name: "Mandatory", docs: MANDATORY_DOCS, color: "#38bdf8" },
                ...(categoryDocs.length > 0 ? [{ name: `Category (${userCategory})`, docs: categoryDocs, color: "#f59e0b" }] : []),
                ...(reservationDocs.length > 0 ? [{ name: "Special Reservation", docs: reservationDocs, color: "#818cf8" }] : []),
                { name: "Fee Concession", docs: CONCESSION_DOCS, color: "#34d399" }
              ].map(sec => (
                <div key={sec.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: sec.color }}></span>
                    <span className="text-[11.5px] font-semibold text-slate-300">{sec.name}</span>
                  </div>
                  <span className="text-[11.5px] font-black" style={{ color: sec.color }}>{sec.docs.length}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-2 text-[11px] font-bold text-slate-200 bg-slate-900 border border-white/10 px-3 py-2 rounded-xl w-full">
              <Calendar className="w-3.5 h-3.5 text-slate-400" />
              Counselling in 12 days
            </div>
          </div>

          {/* How to use Card */}
          <div className="luxury-glass-card p-5 rounded-2xl border border-white/5 space-y-3.5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-sky-400" />
              <span className="text-[10px] font-black text-slate-400 tracking-wider uppercase select-none">HOW TO USE</span>
            </div>
            <ul className="space-y-2.5">
              {[
                "Tap any section header to expand it",
                "Tap any document row to view its guidelines",
                "Guidelines are written in Marathi + English for clarity",
                "Central Format docs need a separate DM/SDM issued certificate"
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-sky-400 text-[12px] mt-0.5 flex-shrink-0">▸</span>
                  <span className="text-[11.5px] text-slate-400 font-medium leading-relaxed">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Organizing Guidelines Card */}
          <div className="luxury-glass-card p-5 rounded-2xl border border-white/5 space-y-3.5">
            <div className="flex items-center gap-2 text-[#FF6B35]">
              <HelpCircle className="w-4.5 h-4.5" />
              <h4 className="font-extrabold text-[10px] uppercase tracking-wider text-slate-300">Organizing Guidelines</h4>
            </div>
            <p className="text-slate-400 text-[11px] leading-relaxed font-semibold">
              Keep 3 sets of attested photocopies ready. The allotted college will retain all original files during physical admission rounds.
            </p>
          </div>

          {/* Shimmer Primary Download Button */}
          <button
            onClick={handlePrint}
            className="shimmer-btn w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 font-black text-sm transition-all hover:shadow-[0_0_15px_rgba(0,200,150,0.3)] border border-[#00C896]/20 shadow-md select-none"
          >
            <Download className="w-4 h-4 text-slate-950 stroke-[3]" />
            Download Checklist PDF
          </button>
        </div>
      </div>

      {/* ─── Pinned Mobile Bottom Sticky Progress & Compact CTA Bar ─────────────── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 h-[56px] bg-[#0f1621] border-t border-white/[0.08] lg:hidden flex items-center justify-between px-4 no-print shadow-2xl select-none">
        {/* Left Side: Pulse Dot & Total Docs */}
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-400"></span>
          </span>
          <span className="text-[14px] font-semibold text-white tracking-wide">
            {totalCount} Documents Required
          </span>
        </div>

        {/* Right Side: Compact Download Button */}
        <button
          onClick={handlePrint}
          className="shimmer-btn h-[36px] px-4 rounded-[20px] bg-gradient-to-r from-sky-500 to-blue-600 text-white font-black text-xs transition-all border border-sky-400/20 shadow-md active:scale-95 flex items-center justify-center gap-1.5"
        >
          <Download className="w-3.5 h-3.5 stroke-[3]" />
          Download
        </button>
      </div>

      {/* ─── Document Detail Pop-up Modal / Bottom Sheet ────────────────────── */}
      {activeModalDoc && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center no-print">
          {/* Backdrop Blur Overlay with Fade-in */}
          <div
            className="absolute inset-0 bg-[#07090e]/75 backdrop-blur-md animate-fade-in-backdrop"
            onClick={() => setActiveModalDoc(null)}
          />

          {/* Responsive Card Container */}
          <div
            className="relative w-full md:max-w-xl bg-[#0f121a]/95 border-t md:border border-white/10 md:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] md:max-h-[90vh] transition-all duration-300"
            style={{
              animation: "window.innerWidth < 768 ? 'slideUp 300ms cubic-bezier(0.16, 1, 0.3, 1) forwards' : 'scaleUp 250ms cubic-bezier(0.16, 1, 0.3, 1) forwards'",
              boxShadow: "0 -10px 40px rgba(0,0,0,0.5), 0 25px 50px rgba(0,0,0,0.5)"
            }}
          >
            {/* Visual Indicator handle for mobile drawer */}
            <div className="w-12 h-1.5 bg-slate-700/60 rounded-full mx-auto my-3 md:hidden flex-shrink-0" />

            {/* Modal Content - Scrollable */}
            <div className="overflow-y-auto p-6 md:p-7 space-y-6 scrollbar-none flex-1">
              {/* Header Section */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <span className="text-[10px] font-black text-sky-400 tracking-wider uppercase">
                    {activeModalDoc.section || "DOCUMENT DETAIL"}
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-white leading-tight">
                    {activeModalDoc.name}
                  </h3>
                </div>
                <button
                  onClick={() => setActiveModalDoc(null)}
                  className="w-8 h-8 rounded-full bg-slate-800/80 border border-white/5 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700/80 transition-all cursor-pointer flex-shrink-0 text-xl font-bold"
                >
                  &times;
                </button>
              </div>

              {/* Description Text */}
              {activeModalDoc.description && (
                <p className="text-sm text-slate-300 leading-relaxed font-medium bg-slate-900/40 p-4 rounded-xl border border-white/[0.03]">
                  {activeModalDoc.description}
                </p>
              )}

              {/* Info Grid: Authority & Validity */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Issuing Authority */}
                <div
                  className="flex items-start gap-3"
                  style={{
                    background: 'rgba(59,130,246,0.07)',
                    border: '1px solid rgba(59,130,246,0.12)',
                    borderRadius: '12px',
                    padding: '12px 14px',
                    borderLeftWidth: '4px',
                    borderLeftColor: '#3b82f6'
                  }}
                >
                  <Building2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold uppercase block text-[9px] tracking-wider text-blue-400/80 mb-0.5">
                      Issuing Authority
                    </span>
                    <span className="text-sm font-semibold text-white leading-relaxed">
                      {activeModalDoc.issuer || "Competent Authority"}
                    </span>
                  </div>
                </div>

                {/* Required Validity */}
                <div
                  className="flex items-start gap-3"
                  style={{
                    background: 'rgba(34,197,94,0.07)',
                    border: '1px solid rgba(34,197,94,0.12)',
                    borderRadius: '12px',
                    padding: '12px 14px',
                    borderLeftWidth: '4px',
                    borderLeftColor: '#22c55e'
                  }}
                >
                  <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold uppercase block text-[9px] tracking-wider text-emerald-400/80 mb-0.5">
                      Required Validity
                    </span>
                    <span className="text-sm font-semibold text-white leading-relaxed">
                      {activeModalDoc.validity || "Lifetime"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Central Format warning if applicable */}
              {activeModalDoc.isCentralFormat && (
                <div
                  className="flex items-start gap-3"
                  style={{
                    background: 'rgba(239,68,68,0.05)',
                    border: '1px solid rgba(239,68,68,0.18)',
                    borderLeft: '4px solid #ef4444',
                    borderRadius: '12px',
                    padding: '12px 14px'
                  }}
                >
                  <ShieldAlert className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-[9px] font-bold text-red-400 uppercase block mb-0.5 tracking-wider">
                      Central Format Mandated
                    </span>
                    <p className="text-xs leading-relaxed font-semibold text-slate-300">
                      <strong>Why?</strong> AIIMS, JIPMER, Deemed Universities, and all 15% All India Quota rounds strictly refuse state-level formats.
                    </p>
                  </div>
                </div>
              )}

              {/* Pro Tip if available */}
              {activeModalDoc.tips && (
                <div
                  className="flex items-start gap-3"
                  style={{
                    background: 'rgba(234,179,8,0.06)',
                    border: '1px solid rgba(234,179,8,0.18)',
                    borderRadius: '12px',
                    padding: '12px 14px',
                    borderLeftWidth: '4px',
                    borderLeftColor: '#eab308'
                  }}
                >
                  <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold uppercase block text-[9px] tracking-wider text-amber-500 mb-0.5">
                      Pro Tip
                    </span>
                    <p className="text-sm font-semibold text-slate-200 leading-relaxed">
                      {activeModalDoc.tips}
                    </p>
                  </div>
                </div>
              )}

              {/* Guidelines List if available */}
              {activeModalDoc.guidelines && activeModalDoc.guidelines.length > 0 && (
                <div
                  className="space-y-3"
                  style={{
                    background: 'rgba(56,189,248,0.05)',
                    border: '1px solid rgba(56,189,248,0.15)',
                    borderLeft: '4px solid #38bdf8',
                    borderRadius: '12px',
                    padding: '14px 16px'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-sky-400 flex-shrink-0" />
                    <span className="text-[10px] font-black tracking-wider text-sky-400 uppercase">
                      Guidelines &amp; Requirements
                    </span>
                  </div>
                  <ul className="space-y-2.5">
                    {activeModalDoc.guidelines.map((rule, rIdx) => (
                      <li key={rIdx} className="flex items-start gap-2.5">
                        <span className="text-sky-400 text-sm leading-relaxed flex-shrink-0">•</span>
                        <span className="text-sm font-semibold leading-relaxed text-slate-200">
                          {rule}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Fixed Attestation Footer Tip */}
              <div
                className="flex items-center gap-2.5"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '10px',
                  padding: '10px 14px'
                }}
              >
                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <p className="text-xs text-slate-400 font-medium">
                  Tip: Keep 3 attested copies of this document ready for reporting day.
                </p>
              </div>
            </div>

            {/* Close Button / Action Bar */}
            <div className="p-4 md:p-6 bg-slate-950/40 border-t border-white/5 flex items-center justify-end gap-3 flex-shrink-0">
              <button
                onClick={() => setActiveModalDoc(null)}
                className="w-full md:w-auto px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-sm transition-all border border-white/10"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}
